// Manifest + peaks loaders for the share-web SPA.
//
// Fetches the share manifest from `<cdnBase>/shares/<shareId>/manifest.json`
// and the peaks payload from `<cdnBase>/shares/<shareId>/<peaksUrl>`.
// Throws on non-2xx responses or unsupported manifest version.

import type {
  ShareManifest,
  SummaryPeaks,
  PerTrackSummaryPeaks,
} from '@bandway/share-types';

function joinUrl(base: string, ...segments: string[]): string {
  const trimmedBase = base.replace(/\/+$/, '');
  const tail = segments
    .map((seg) => seg.replace(/^\/+/, '').replace(/\/+$/, ''))
    .filter((seg) => seg.length > 0)
    .join('/');
  return `${trimmedBase}/${tail}`;
}

async function fetchJson<T>(url: string, label: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`${label} fetch failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Gated manifest loader — fetches via the worker gate with auth cookie.
// ---------------------------------------------------------------------------

export type ManifestLoadResult =
  | { state: 'ok'; manifest: ShareManifest; playbackToken: string | null }
  | { state: 'needs-auth'; teamName: string | null }
  | { state: 'not-a-member'; teamName: string | null }
  | { state: 'revoked' }
  | { state: 'error'; status: number };

/**
 * Гостевой пропуск (единый сетлист, фазы 3+4, задача D2) — `?setlist=<id>&g=<key>`,
 * тот же приём, что у `song-text-api.ts`: воркер (`functions/api/shares/
 * [shareId]/[[resource]].ts`) читает пару РОВНО так же, и участник за кукой
 * её не передаёт вовсе — `credentials: 'include'` остаётся единственным
 * пропуском для него.
 */
export interface ManifestGuestAccess { setlistId: string; guestKey: string }

/**
 * Fetches the manifest through the worker gate (`/api/shares/:id/manifest.json`).
 * Sends `credentials: 'include'` so the `bw_session` cookie is forwarded.
 *
 * Returns a discriminated union:
 *  - `{ state: 'ok', manifest, playbackToken }` on 200
 *  - `{ state: 'needs-auth', teamName }` on 401
 *  - `{ state: 'not-a-member', teamName }` on 403 `reason: 'not_a_member'`
 *  - `{ state: 'revoked' }` on any other 403
 *  - `{ state: 'error', status }` on any other non-2xx
 */
export async function loadManifest(shareId: string, apiBase = '', guest?: ManifestGuestAccess): Promise<ManifestLoadResult> {
  const qs = guest ? `?setlist=${encodeURIComponent(guest.setlistId)}&g=${encodeURIComponent(guest.guestKey)}` : '';
  const res = await fetch(`${apiBase}/api/shares/${encodeURIComponent(shareId)}/manifest.json${qs}`, {
    credentials: 'include',
  });
  if (res.status === 401) {
    const b = await res.json().catch(() => ({}));
    return { state: 'needs-auth', teamName: (b as any)?.teamName ?? null };
  }
  if (res.status === 403) {
    // Воркер отвечает на 403 двумя разными телами ([[resource]].ts):
    // `revoked` и `not_a_member` + teamName. Схлопнуть их по статусу значило
    // сказать «ссылку отозвали» человеку, который вошёл не тем аккаунтом
    // (B-260927-share-web-not-member-shown-revoked; тот же разбор, что у
    // song-text-api.ts). Неразборное тело — прежний безопасный ответ.
    const b = await res.json().catch(() => ({}));
    if ((b as any)?.reason === 'not_a_member') {
      return { state: 'not-a-member', teamName: (b as any)?.teamName ?? null };
    }
    return { state: 'revoked' };
  }
  if (!res.ok) return { state: 'error', status: res.status };
  const manifest = (await res.json()) as ShareManifest;
  return { state: 'ok', manifest, playbackToken: res.headers.get('X-Playback-Token') };
}

/**
 * Fetches the per-track summary peaks JSON pointed to by a v2 manifest's
 * `perTrackPeaksUrl`. Returns `null` for v1 manifests, when the URL is
 * missing, or when the fetch fails (graceful degrade — the SPA falls back
 * to placeholder waveforms in that case).
 */
export async function loadPerTrackPeaks(
  cdnBase: string,
  shareId: string,
  manifest: ShareManifest,
): Promise<PerTrackSummaryPeaks | null> {
  if (manifest.v < 2 || !manifest.perTrackPeaksUrl) {
    return null;
  }
  const url = joinUrl(cdnBase, 'shares', shareId, manifest.perTrackPeaksUrl);
  try {
    return await fetchJson<PerTrackSummaryPeaks>(url, 'peaks-per-track');
  } catch {
    return null;
  }
}

/**
 * Fetches the summary peaks JSON pointed to by a manifest's `peaksUrl`.
 *
 * The manifest stores `peaksUrl` as a relative path so the share bundle can
 * version the peaks file independently — we just resolve it under the share's
 * CDN folder.
 *
 * Throws `Error("peaks fetch failed: <status>")` on non-2xx.
 */
export async function loadPeaks(
  cdnBase: string,
  shareId: string,
  peaksUrl: string,
): Promise<SummaryPeaks> {
  const url = joinUrl(cdnBase, 'shares', shareId, peaksUrl);
  return fetchJson<SummaryPeaks>(url, 'peaks');
}
